import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component, Ref, Prop, SimpleEvent, CartItem } from '@eastsideco/salvo-ts';
import { cart } from "@/theme/Cart";
import { formatMoney } from '@/utils/formatting';
import { SideCart } from '../SideCart/SideCart';
import { CartItems } from "./CartItems";
import { debounce } from 'lodash';

@Component
export class CartMain extends ThemeComponent {
    @Prop() side !: boolean;
    @Ref() summary!: HTMLElement[];
    @Ref() emptyCart!: HTMLElement;
    @Ref() items!: CartItems;
    @Ref({ required: false }) count!: HTMLElement;
    @Ref({ required: false }) totalPrice!: HTMLElement;
    @Ref({ required: false }) freeDelivery!: HTMLElement;
    @Ref({required: false}) orderNote!: HTMLTextAreaElement;

    private itemCount: number = 0;
    private sideCart: SideCart | null = document.body.querySelector('side-cart');

    private _onCartItemCountChange = new SimpleEvent<number>();
    protected quantity: number = 1;

    public get onCartItemCountChange() {
        return this._onCartItemCountChange.asEvent();
    }

    init() {

        this.setEventListeners();
    }

    protected setEventListeners() {

        // Ensure order note element exists
        if (this.orderNote) {
            // Listen for order note change
            this.$listen(this.orderNote, 'input', debounce(async() => {
                // Get order note value
                let orderNote = this.orderNote.value.trim();

                if (orderNote === '') {
                    orderNote = '_blank_';
                }
                // Update cart attribute
                await cart.updateNote(orderNote);
            }, 500));
        }
    }

    /**
     * Check if variant is in cart.
     * @param variant - variant id.
     */
    public checkCart(variant: number) {
        return (cart.items.filter((item) => ~~item.variantId === ~~variant)).length > 0;
    }

    public updateCart(action: string, item: CartItem, quantity?: number, target?: HTMLElement | null) {
        switch (action) {
            case 'remove':
                this.handleRemoveItems(item, target);
                break;
            case 'update':
                this.handleUpdateItem(item, quantity);
                break;
        }
    }

    public reloadCart() {
        let url = `/cart?section_id=section_cart-main`;
        if (this.side) {
            url = `/?section_id=section_cart-side`;
        }

        fetch(url)
            .then((response) => response.text())
            .then((text) => {
                const cartInnerHTML = new DOMParser()
                    .parseFromString(text, 'text/html')
                    .querySelector('[data-ref="items"]');

                if (!cartInnerHTML) { return; }
                this.items.innerHTML = '';

                this.items.prepend(cartInnerHTML);
                this.updateCartData();

                if (!this.sideCart) { return; }
                this.sideCart.openCart();
            })
            .catch((event: unknown | null) => {
                if (event instanceof Error) {
                    this.$log.error(event.message);
                }
            });
    }

    /**
     * Hide or show cart items.
     * @returns
     */
    private handleContentDisplay() {
        const itemsLength = cart.items.length;
        if (itemsLength <= 0) {
            this.classList.add('is-empty');
            this.classList.remove('has-items');
            for (const summary of this.summary) {
                summary.classList.remove('is-active');
            }

            return;
        }

        this.classList.remove('is-empty');
        this.classList.add('has-items');
        this.itemCount = 0;
        for (const summary of this.summary) {
            summary.classList.add('is-active');
        }
    }

    /**
     * Handle render of removed items
     */
    private handleRemoveItems(item: CartItem, target: HTMLElement | undefined | null) {
        if (!target) { return; }

        try {
            cart.removeItem(item).then(() => {
                const itemRow = target.closest('[data-ref="cartItemRow"]');
                itemRow?.remove();
                this.updateCartData();

            }).catch((event: unknown) => {
                if (event instanceof Error) {
                    this.$log.error(event.message);
                }
            });

        } catch (event: unknown) {
            if (event instanceof Error) {
                this.$log.info('Remove error', event.message);
            }
        }
    }

    /**
     * Update cart item quantity.
     * @returns
     */
    private handleUpdateItem(item: CartItem, quanity: number | undefined) {
        if (!quanity) { return; }
        cart.changeItem(item, quanity, null).then(() => {
            this.updateCartData();
        }).catch((event: unknown) => {
            if (event instanceof Error) {
                this.$log.error(event.message);
            }
        });
    }

    /**
     * Update cart data.
     */
    private updateCartData() {
        this.updateCartCount();
        this.updateTotalPrice();
        this.handleContentDisplay();
    }

    /**
     * Update cart count
     * @returns
     */
    private updateCartCount() {
        this.itemCount = cart.items.filter((item) => ~~item.quantity > 0)
            .map((obj) => ~~obj.quantity)
            .reduce((a, b) => a + b, 0);

        this._onCartItemCountChange.dispatch(this.itemCount);

        if (!this.count) { return; }
        this.count.innerHTML = String(this.itemCount);
    }

    /**
     * Update total price.
     * @returns
     */
    private updateTotalPrice() {
        if (!this.totalPrice) { return; }
        this.totalPrice.innerHTML = formatMoney(cart.totalPrice) as string;
    }
}
